import { z } from 'zod';
import { latSchema, lonSchema, precisionSchema, statutSchema } from './commun';

/** Rayons proposés pour "Autour de moi", en minutes (SPEC 10.2). */
export const RAYONS_MINUTES = [15, 30, 45, 60] as const;
export type RayonMinutes = (typeof RAYONS_MINUTES)[number];
export const RAYON_PAR_DEFAUT: RayonMinutes = 30;

/** Seuil sous lequel l'écran propose d'élargir le rayon. */
export const SEUIL_ELARGIR = 3;

export const rayonSchema = z.coerce
  .number()
  .refine((n): n is RayonMinutes => (RAYONS_MINUTES as readonly number[]).includes(n), {
    message: 'Rayon invalide',
  });

function enBooleen(valeur: unknown): unknown {
  if (typeof valeur === 'string') return valeur === 'true' || valeur === '1';
  return valeur;
}

/** Paramètres de `GET /api/proches` : la position, le rayon et les deux filtres rapides. */
export const prochesRequeteSchema = z.object({
  lon: lonSchema,
  lat: latSchema,
  minutes: rayonSchema.default(RAYON_PAR_DEFAUT),
  actifs: z.preprocess(enBooleen, z.boolean()).default(true),
  controle_du: z.preprocess(enBooleen, z.boolean()).default(false),
});
export type ProchesRequete = z.infer<typeof prochesRequeteSchema>;

export const clientProcheSchema = z.object({
  id: z.number().int(),
  raisonSociale: z.string(),
  ville: z.string().nullable(),
  departement: z.string().nullable(),
  statut: statutSchema.nullable(),
  precision: precisionSchema,
  lon: z.number(),
  lat: z.number(),
  /** Distance à vol d'oiseau depuis le point de départ, en mètres. */
  distanceM: z.number(),
  /** Durée estimée (haversine x 1,3 puis vitesse moyenne), en minutes entières, au moins 1. */
  dureeMin: z.number().int(),
  /** Date ISO `yyyy-MM-dd` du prochain contrôle, ou null. */
  prochainControle: z.string().nullable(),
  enRetard: z.boolean(),
});
export type ClientProche = z.infer<typeof clientProcheSchema>;

export const prochesReponseSchema = z.object({
  /** Rayon effectivement appliqué. */
  minutes: rayonSchema,
  /** Origine du polygone : moteur routier IGN ou cercle d'estimation. */
  source: z.enum(['ign', 'estimation']),
  /** Les durées affichées sont toujours des estimations à vol d'oiseau (SPEC 4, règle 8). */
  dureesEstimees: z.literal(true),
  clients: z.array(clientProcheSchema),
  /** Rayon suivant à proposer quand il y a moins de 3 résultats, sinon null. */
  elargirA: rayonSchema.nullable(),
});
export type ProchesReponse = z.infer<typeof prochesReponseSchema>;

/** Nombre maximal de lieux favoris par utilisateur. */
export const MAX_LIEUX_FAVORIS = 20;

export const lieuFavoriSchema = z.object({
  id: z.number().int(),
  libelle: z.string(),
  lon: z.number(),
  lat: z.number(),
});
export type LieuFavori = z.infer<typeof lieuFavoriSchema>;

export const lieuxFavorisReponseSchema = z.object({ lieux: z.array(lieuFavoriSchema) });

export const lieuFavoriCreationSchema = z.object({
  libelle: z.string().trim().min(1, 'Libellé requis').max(80, 'Libellé trop long'),
  lon: z.number().min(-180).max(180),
  lat: z.number().min(-90).max(90),
});
export type LieuFavoriCreation = z.infer<typeof lieuFavoriCreationSchema>;

export const lieuFavoriIdSchema = z.object({ id: z.coerce.number().int().positive() });

/** Élargissement proposé : le rayon suivant, ou null au maximum. */
export function rayonSuivant(minutes: number): RayonMinutes | null {
  return RAYONS_MINUTES.find((r) => r > minutes) ?? null;
}
